import { gql, GraphQLClient } from "graphql-request";
import { createGraphqlClient, type GraphqlRequestConfig } from "./client";

// GraphQL Mutations
const SAVE_CODE_MUTATION = gql`
  mutation SaveCode($input: SaveCodeInput!) {
    saveCode(input: $input) {
      success
      id
      message
    }
  }
`;

const SUBMIT_CODE_MUTATION = gql`
  mutation SubmitCode($input: SubmitCodeInput!) {
    submitCode(input: $input) {
      success
      id
      message
      failedAttempts
      isCorrect
      canShowAnswer
      limitExceeded
      remainingSubmissions
      testsPassed
      testsTotal
      failures {
        testCase
        actual
        mismatchLines
        expectedLineCount
      }
    }
  }
`;

const RUN_LESSON_CODE_MUTATION = gql`
  mutation RunLessonCode($input: RunLessonCodeInput!) {
    runLessonCode(input: $input) {
      success
      data {
        testCount
        testResults {
          testCase
          passed
        }
        output
        stderr
        exitCode
        runtimeFailed
      }
    }
  }
`;

/* 落ちたテストの中身 (detail) も取る形。detail をまだ持たない api-go には、検証の誤りで断られるので前の形で取り直す */
const RUN_LESSON_CODE_WITH_DETAIL_MUTATION = gql`
  mutation RunLessonCode($input: RunLessonCodeInput!) {
    runLessonCode(input: $input) {
      success
      data {
        testCount
        testResults {
          testCase
          passed
          detail
        }
        output
        stderr
        exitCode
        runtimeFailed
      }
    }
  }
`;

const GUEST_SUBMIT_CODE_MUTATION = gql`
  mutation GuestSubmitCode($input: GuestSubmitCodeInput!) {
    guestSubmitCode(input: $input) {
      success
      id
      message
      failedAttempts
      isCorrect
      canShowAnswer
      limitExceeded
      remainingSubmissions
    }
  }
`;

// GraphQL Queries
const GET_CODE_PROGRESS_QUERY = gql`
  query GetCodeProgress($lessonId: String!, $fileId: String!) {
    getCodeProgress(lessonId: $lessonId, fileId: $fileId) {
      id
      userId
      lessonId
      fileId
      code
      language
      isSubmitted
      failedAttempts
      isCorrect
      lastSavedAt
      submittedAt
      createdAt
      updatedAt
    }
  }
`;

const GET_ALL_CODE_PROGRESS_QUERY = gql`
  query GetAllCodeProgressForLesson($lessonId: String!) {
    getAllCodeProgressForLesson(lessonId: $lessonId) {
      id
      userId
      lessonId
      fileId
      code
      language
      isSubmitted
      lastSavedAt
      submittedAt
      createdAt
      updatedAt
    }
  }
`;

const GET_USER_SUBMITTED_LESSONS_QUERY = gql`
  query GetUserSubmittedLessons {
    getUserSubmittedLessons
  }
`;

const RESET_CODE_PROGRESS_MUTATION = gql`
  mutation ResetCodeProgress($lessonId: String!, $fileId: String!) {
    resetCodeProgress(lessonId: $lessonId, fileId: $fileId) {
      success
      id
      message
    }
  }
`;

const GET_USER_COURSES_PROGRESS_QUERY = gql`
  query GetUserCoursesProgress {
    getUserCoursesProgress {
      courseId
      completedLessons
      totalLessons
      progressPercentage
    }
  }
`;

const GET_USER_STUDY_MINUTES_QUERY = gql`
  query GetUserStudyMinutes {
    getUserStudyMinutes
  }
`;

// Types
export interface SaveCodeInput {
  lessonId: string;
  fileId: string;
  code: string;
  language?: string;
}

export interface SubmitCodeInput {
  lessonId: string;
  fileId: string;
  code: string;
  language?: string;
}

export interface RunLessonCodeInput {
  lessonId: string;
  code: string;
}

export interface RunLessonCodeResponse {
  success: boolean;
  data: {
    testCount: number;
    /** 落ちた行の最初の 3 件には、api-go が input、expected、actual、error のどれかを添える (無い鍵は付けない) */
    testResults: Array<{ testCase: number; passed: boolean; input?: unknown; expected?: unknown; actual?: unknown; error?: string }>;
    output: string;
    stderr: string;
    exitCode: number | null;
    runtimeFailed: boolean;
  };
}

export interface CodeProgress {
  id: string;
  userId: string;
  lessonId: string;
  fileId: string;
  code: string;
  language?: string;
  isSubmitted: boolean;
  failedAttempts: number;
  isCorrect: boolean;
  canShowAnswer: boolean;
  lastSavedAt: string;
  submittedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SaveCodeResponse {
  success: boolean;
  id: string;
  message?: string;
}

/** 通らなかったテスト 1 件の手がかり。期待の出力そのものは含まない (答えになるため) */
export interface SubmitCodeFailure {
  testCase: number;
  /** 受講生自身の出力の先頭部分 */
  actual: string;
  /** 期待と違った行の番号 (1 始まり) */
  mismatchLines: number[];
  expectedLineCount: number;
}

export interface SubmitCodeResponse {
  success: boolean;
  id: string;
  message?: string;
  failedAttempts: number;
  isCorrect: boolean;
  canShowAnswer: boolean;
  limitExceeded?: boolean;
  remainingSubmissions?: number;
  /** Linux の提出が通らなかったときだけ */
  testsPassed?: number | null;
  testsTotal?: number | null;
  failures?: SubmitCodeFailure[] | null;
}

export interface GuestSubmitCodeInput {
  guestFingerprint: string;
  lessonId: string;
  fileId: string;
  code: string;
  language?: string;
}

export interface GraphqlCodeProgressOptions extends GraphqlRequestConfig {
  client?: GraphQLClient;
}

function ensureClient(options?: GraphqlCodeProgressOptions) {
  if (options?.client) {
    return options.client;
  }
  return createGraphqlClient(options);
}

// API Functions
export async function saveCode(
  input: SaveCodeInput,
  options?: GraphqlCodeProgressOptions,
): Promise<SaveCodeResponse> {
  const client = ensureClient(options);
  const data = await client.request(SAVE_CODE_MUTATION, { input });
  return (data as any).saveCode;
}

export async function submitCode(
  input: SubmitCodeInput,
  options?: GraphqlCodeProgressOptions,
): Promise<SubmitCodeResponse> {
  const client = ensureClient(options);
  const data = await client.request(SUBMIT_CODE_MUTATION, { input });
  return (data as any).submitCode;
}

export async function runLessonCode(
  input: RunLessonCodeInput,
  options?: GraphqlCodeProgressOptions,
): Promise<RunLessonCodeResponse> {
  const client = ensureClient(options);
  let data: { runLessonCode: RunLessonCodeResponse };
  try {
    data = await client.request<{ runLessonCode: RunLessonCodeResponse }>(RUN_LESSON_CODE_WITH_DETAIL_MUTATION, { input });
  } catch (error) {
    // 検証の誤りはコードを動かす前に返るので、取り直しても実行の回数は 1 回しか数えない
    if (!isUnknownDetailFieldError(error)) throw error;
    data = await client.request<{ runLessonCode: RunLessonCodeResponse }>(RUN_LESSON_CODE_MUTATION, { input });
  }
  const response = data.runLessonCode;
  return {
    ...response,
    data: { ...response.data, testResults: response.data.testResults.map(flattenRunCheck) },
  };
}

/** api-go がまだ RunLessonCodeCheck.detail を持たないときの検証の誤り */
function isUnknownDetailFieldError(error: unknown): boolean {
  const errors = (error as { response?: { errors?: unknown } } | null)?.response?.errors;
  return Array.isArray(errors) && errors.some((entry) => /Cannot query field "detail"/.test(String((entry as { message?: unknown } | null)?.message ?? "")));
}

type RunCheckWire = { testCase: number; passed: boolean; detail?: unknown };

/** detail の中身を行に広げる。画面 (CodingFailureDetails) は「鍵があるか」で入力や期待を出すので、無い鍵は付けない */
function flattenRunCheck(check: RunCheckWire): RunLessonCodeResponse["data"]["testResults"][number] {
  const { detail, ...row } = check;
  const out: RunLessonCodeResponse["data"]["testResults"][number] = { testCase: row.testCase, passed: row.passed };
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return out;
  const d = detail as Record<string, unknown>;
  if ("input" in d) out.input = d.input;
  if ("expected" in d) out.expected = d.expected;
  if ("actual" in d) out.actual = d.actual;
  if (typeof d.error === "string" && d.error) out.error = d.error;
  return out;
}

export async function guestSubmitCode(
  input: GuestSubmitCodeInput,
  options?: GraphqlCodeProgressOptions,
): Promise<SubmitCodeResponse> {
  const client = ensureClient(options);
  const data = await client.request(GUEST_SUBMIT_CODE_MUTATION, { input });
  return (data as any).guestSubmitCode;
}

export async function getCodeProgress(
  lessonId: string,
  fileId: string,
  options?: GraphqlCodeProgressOptions,
): Promise<CodeProgress | null> {
  const client = ensureClient(options);
  const data = await client.request(GET_CODE_PROGRESS_QUERY, {
    lessonId,
    fileId,
  });
  return (data as any).getCodeProgress;
}

export async function getAllCodeProgressForLesson(
  lessonId: string,
  options?: GraphqlCodeProgressOptions,
): Promise<CodeProgress[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_ALL_CODE_PROGRESS_QUERY, {
    lessonId,
  });
  return (data as any).getAllCodeProgressForLesson;
}

export async function getUserSubmittedLessons(
  options?: GraphqlCodeProgressOptions,
): Promise<string[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_USER_SUBMITTED_LESSONS_QUERY);
  return (data as any).getUserSubmittedLessons;
}

export interface UserCourseProgress {
  courseId: string;
  completedLessons: number;
  totalLessons: number;
  progressPercentage: number;
}

export async function getUserCoursesProgress(
  options?: GraphqlCodeProgressOptions,
): Promise<UserCourseProgress[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_USER_COURSES_PROGRESS_QUERY);
  return (data as any).getUserCoursesProgress;
}

export async function getUserStudyMinutes(
  options?: GraphqlCodeProgressOptions,
): Promise<number> {
  const client = ensureClient(options);
  const data = await client.request(GET_USER_STUDY_MINUTES_QUERY);
  return (data as any).getUserStudyMinutes ?? 0;
}

export async function resetCodeProgress(
  lessonId: string,
  fileId: string,
  options?: GraphqlCodeProgressOptions,
): Promise<SaveCodeResponse> {
  const client = ensureClient(options);
  const data = await client.request(RESET_CODE_PROGRESS_MUTATION, {
    lessonId,
    fileId,
  });
  return (data as any).resetCodeProgress;
}
